/**
 * 🔫 KioskScanMode — 2026-05-19 (refactorisé en mode PANIER)
 *                  — 2026-05-31 [Phase 18] LIAISON OBLIGATOIRE AVANT APPROBATION
 *
 * Affiché en haut de la page Entrepôt UNIQUEMENT en mode Kiosque.
 *
 * 🛒 FLOW PANIER :
 *   1. L'employé choisit SORTIE ou ENTRÉE (le mode reste actif)
 *   2. Il scanne 1, 2, 3, ... items → ils s'ajoutent au PANIER (sans toucher au stock)
 *   3. Même produit scanné plusieurs fois → quantité incrémentée
 *   4. Ajustement +/-/suppression possible
 *
 * 🔗 [Phase 18] LIAISON (mode SORTIE uniquement) :
 *   Avant le bouton "✅ APPROUVER", l'employé DOIT choisir un lien :
 *     - Installateur  → Semaine ISO courante (auto, lecture seule)
 *     - Peintre/Chef  → Projet (BT actif) via suggestions intelligentes ou recherche libre
 *     - Owner/Admin   → Onglets : Projet / Semaine / Réajustement / Entrée au stock
 *   Le bouton APPROUVER reste désactivé tant que la liaison n'est pas confirmée.
 *   Le backend re-valide tout (sécurité — pas de bypass possible).
 */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ScanLine,
  Volume2,
  VolumeX,
  Trash2,
  Plus,
  Minus,
  CheckCircle2,
  Link2,
  Calendar,
  Briefcase,
  Wrench,
  Undo2,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatAdresseCompacte, buildLiaisonLabel } from '@/lib/kioskBTFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BEEP_OK = 'data:audio/wav;base64,UklGRpQGAABXQVZFZm10IBAAAAABAAEARKwAAESsAAABAAgAZGF0YXAGAAB/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/';
const BEEP_KO = 'data:audio/wav;base64,UklGRpQGAABXQVZFZm10IBAAAAABAAEARKwAAESsAAABAAgAZGF0YXAGAAB/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/';

function playBeep(kind = 'ok', muted = false) {
  if (muted) return;
  try {
    const a = new Audio(kind === 'ok' ? BEEP_OK : BEEP_KO);
    a.volume = 0.6;
    a.play().catch(() => {});
  } catch {
    /* no-op */
  }
}

// 🔢 Numéro de semaine ISO courant (calcul local — affichage uniquement,
// le backend recalcule en source de vérité).
function getCurrentIsoWeek() {
  const d = new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

// 🎭 Famille de rôles → orchestre l'UI de liaison
function getRoleFamily(role) {
  const r = (role || '').toLowerCase().trim();
  if (['proprietaire', 'admin_general', 'adjoint_admin'].includes(r)) return 'owner';
  if (['peintre', 'chef_equipe'].includes(r)) return 'peintre';
  if (r === 'installateur') return 'installateur';
  // fallback prudent : on traite comme peintre (liaison projet requise)
  return 'peintre';
}

export default function KioskScanMode({ onMovementDone }) {
  const [mode, setMode] = useState(null); // 'out' | 'return'
  const [buffer, setBuffer] = useState('');
  const [cart, setCart] = useState([]);
  const [muted, setMuted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [validating, setValidating] = useState(false);
  const inputRef = useRef(null);

  // 🆕 [Phase 18] État de la liaison (mode SORTIE uniquement)
  // { type: 'projet'|'semaine'|'ajustement'|'retour_stock', id, label, motif }
  const [liaison, setLiaison] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSugs, setLoadingSugs] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [ownerTab, setOwnerTab] = useState('projet'); // owner uniquement
  const [motifInput, setMotifInput] = useState('');

  // 🎭 Rôle de l'utilisateur courant (depuis localStorage Kiosque)
  const userRole = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u.role || 'employee';
    } catch { return 'employee'; }
  }, []);
  const family = useMemo(() => getRoleFamily(userRole), [userRole]);
  const currentWeek = useMemo(() => getCurrentIsoWeek(), []);

  // Refocus permanent du champ caché (sauf si on est dans un input visible — ex: recherche projet)
  useEffect(() => {
    const refocus = () => {
      if (mode && inputRef.current && document.activeElement !== inputRef.current) {
        const active = document.activeElement;
        const isEditing = active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA');
        if (!isEditing) inputRef.current.focus();
      }
    };
    refocus();
    const t = setInterval(refocus, 1000);
    return () => clearInterval(t);
  }, [mode]);

  // 🆕 [Phase 18] Charger les suggestions dès qu'on entre en mode SORTIE
  // (peintre/chef/owner — installateur n'en a pas besoin)
  useEffect(() => {
    if (mode !== 'out') { setSuggestions([]); return; }
    if (family === 'installateur') return;
    setLoadingSugs(true);
    axios
      .get(`${API_URL}/api/entrepot/scan/suggestions-projets`)
      .then((r) => setSuggestions(r.data?.suggestions || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoadingSugs(false));
  }, [mode, family]);

  // 🆕 [Phase 18] Pré-remplir la liaison "semaine" pour les installateurs dès mode=out
  useEffect(() => {
    if (mode === 'out' && family === 'installateur' && !liaison) {
      setLiaison({ type: 'semaine', id: null, label: `Semaine ${currentWeek}`, motif: '' });
    }
    if (mode !== 'out') {
      setLiaison(null);
      setMotifInput('');
      setSearchOpen(false);
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [mode, family, currentWeek, liaison]);

  // Lookup produit + ajout au panier
  const ajouterAuPanier = useCallback(
    async (code) => {
      if (!code || !mode || busy) return;
      setBusy(true);
      try {
        const { data } = await axios.post(`${API_URL}/api/entrepot/scan/lookup`, {
          code: code.trim(),
        });
        const prod = data?.product;
        if (!prod || !prod.id) throw new Error('Produit invalide');

        setCart((prev) => {
          const idx = prev.findIndex((it) => it.productId === prod.id);
          const newQty = idx >= 0 ? prev[idx].qty + 1 : 1;
          if (mode === 'out' && newQty > (prod.quantity || 0)) {
            playBeep('ko', muted);
            toast.error(`❌ Stock insuffisant pour « ${prod.name} »`,
              { description: `Stock disponible : ${prod.quantity || 0}`, duration: 4500 });
            return prev;
          }
          playBeep('ok', muted);
          if (idx >= 0) {
            const copy = [...prev];
            copy[idx] = { ...copy[idx], qty: newQty };
            return copy;
          }
          return [
            ...prev,
            { productId: prod.id, productName: prod.name, qty: 1, stockAvant: prod.quantity || 0 },
          ];
        });
      } catch (e) {
        playBeep('ko', muted);
        const detail = e?.response?.data?.detail;
        const msg = (typeof detail === 'string' && detail) || detail?.message || 'Produit introuvable pour ce code';
        toast.error(`❌ ${msg}`, { description: `Code : ${code}`, duration: 4000 });
      } finally {
        setBusy(false);
      }
    },
    [mode, busy, muted]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const code = buffer.trim();
      setBuffer('');
      if (code) ajouterAuPanier(code);
    }
  };

  const ajusterQty = (productId, delta) => {
    setCart((prev) =>
      prev
        .map((it) => {
          if (it.productId !== productId) return it;
          const newQty = it.qty + delta;
          if (mode === 'out' && newQty > it.stockAvant) {
            toast.warning(`Stock disponible : ${it.stockAvant}`);
            return it;
          }
          return { ...it, qty: Math.max(0, newQty) };
        })
        .filter((it) => it.qty > 0)
    );
  };

  const supprimerLigne = (productId) =>
    setCart((prev) => prev.filter((it) => it.productId !== productId));

  const viderPanier = () => {
    if (cart.length === 0) return;
    if (window.confirm('Vider tout le panier sans valider ?')) {
      setCart([]);
      setLiaison(family === 'installateur' && mode === 'out'
        ? { type: 'semaine', id: null, label: `Semaine ${currentWeek}`, motif: '' }
        : null);
    }
  };

  // 🆕 [Phase 18] Recherche libre projet (debounced)
  useEffect(() => {
    if (!searchOpen) return;
    const t = setTimeout(async () => {
      const q = searchTerm.trim();
      if (q.length < 2) { setSearchResults([]); return; }
      setSearching(true);
      try {
        // 🆕 [Phase 27] Endpoint dédié de recherche unifiée (numéro BT, client, nom dossier, adresse)
        const r = await axios.get(`${API_URL}/api/crm/bons-travail/search`, {
          params: { q, limit: 15 },
        });
        const list = r.data?.bons || [];
        // 🔗 [Phase v13.60.11 — 2026-08-15] Enrichissement : on stocke aussi
        //   adresse_travaux + ville_travaux pour lever l'ambiguïté quand un
        //   même client a plusieurs BT actifs (ex: François Couture × 12 BT).
        // 🔠 [Phase v13.60.11.1 — 2026-08-15] Ajout `nom_bt` (nom textuel
        //   du BT = description_travaux) — remplace l'affichage du numéro.
        setSearchResults(
          list.slice(0, 12).map((b) => ({
            id: b.id,
            numero: b.numero_bt || b.numero || '',
            numero_bt: b.numero_bt || b.numero || '',
            nom_bt: b.nom_bt || b.description_travaux || '',
            client_nom: b.client_nom || b.dossier?.client_nom || '',
            nom_dossier: b.nom_dossier || b.dossier?.nom_dossier || '',
            adresse_travaux: b.adresse_travaux || b.dossier?.adresse_travaux || '',
            ville_travaux: b.ville_travaux || b.dossier?.ville_travaux || '',
          }))
        );
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, searchOpen]);

  // ✅ Validation : envoie tout le panier au backend avec la liaison
  const approuverPanier = async () => {
    if (cart.length === 0 || !mode) return;
    if (mode === 'out' && !liaisonComplete()) {
      toast.warning('Choisissez d\'abord une liaison (projet, semaine ou motif).');
      return;
    }
    setValidating(true);
    let okCount = 0; let errCount = 0; const errors = [];
    const lienPayload = buildLienPayload();
    for (const item of cart) {
      for (let i = 0; i < item.qty; i++) {
        try {
          await axios.post(`${API_URL}/api/entrepot/scan/mouvement`, {
            code: item.productId, action: mode, ...lienPayload,
          });
          okCount++;
        } catch (e) {
          errCount++;
          const detail = e?.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : detail?.message || 'Erreur';
          errors.push(`${item.productName} (#${i + 1}): ${msg}`);
          break;
        }
      }
    }
    setValidating(false);
    if (okCount > 0) {
      playBeep('ok', muted);
      toast.success(
        `✅ ${okCount} mouvement${okCount > 1 ? 's' : ''} validé${okCount > 1 ? 's' : ''}`,
        { description: liaison?.label ? `Liaison : ${liaison.label}` : (mode === 'out' ? 'Sorties enregistrées' : 'Entrées enregistrées'), duration: 4500 }
      );
    }
    if (errCount > 0) {
      playBeep('ko', muted);
      toast.error(`${errCount} erreur(s)`, { description: errors.slice(0, 3).join('\n'), duration: 8000 });
    }
    setCart([]);
    // Réinitialise la liaison sauf pour l'installateur (semaine auto reste)
    if (family === 'installateur' && mode === 'out') {
      setLiaison({ type: 'semaine', id: null, label: `Semaine ${currentWeek}`, motif: '' });
    } else {
      setLiaison(null);
      setMotifInput('');
    }
    if (typeof onMovementDone === 'function') onMovementDone();
  };

  // ─── Helpers liaison ───────────────────────────────────────────────
  const liaisonComplete = () => {
    if (mode !== 'out') return true;
    if (!liaison) return false;
    if (liaison.type === 'projet') return !!liaison.id;
    if (liaison.type === 'ajustement') return !!(liaison.motif || '').trim();
    if (liaison.type === 'semaine' || liaison.type === 'retour_stock') return true;
    return false;
  };
  const buildLienPayload = () => {
    if (mode !== 'out' || !liaison) return {};
    const out = { lien_type: liaison.type };
    if (liaison.id) out.lien_id = liaison.id;
    if (liaison.motif) out.lien_motif = liaison.motif;
    return out;
  };
  const choisirProjet = (bt) => {
    // 🔗 [Phase v13.60.11 — 2026-08-15] Label enrichi : Client · BT · Adresse
    //   Permet à l'employé de reconnaître clairement le BT choisi même
    //   quand le même client en a plusieurs (ex: 12 BT François Couture).
    //   Helper pur `buildLiaisonLabel` isolé dans /lib/kioskBTFormat.js.
    setLiaison({
      type: 'projet',
      id: bt.id,
      label: buildLiaisonLabel(bt),
      motif: '',
    });
    setSearchOpen(false);
    setSearchTerm('');
  };

  const totalItems = cart.reduce((s, it) => s + it.qty, 0);

  // ─── Bloc Liaison conditionnel (rendu) ─────────────────────────────
  const renderLiaisonStep = () => {
    if (mode !== 'out' || cart.length === 0) return null;

    // Installateur : semaine forcée (lecture seule + rassurant)
    if (family === 'installateur') {
      return (
        <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-4" data-testid="kiosk-liaison-installateur">
          <div className="flex items-center gap-3">
            <Calendar className="w-7 h-7 text-blue-600" />
            <div>
              <p className="text-xs uppercase tracking-wider text-blue-700 font-bold">Liaison automatique</p>
              <p className="text-lg font-bold text-blue-900">Semaine {currentWeek}</p>
              <p className="text-xs text-blue-600">Tous les articles sortis seront imputés à votre semaine en cours.</p>
            </div>
          </div>
        </div>
      );
    }

    // Owner : tabs
    if (family === 'owner') {
      return (
        <div className="rounded-xl border-2 border-orange-200 bg-orange-50 p-4 space-y-3" data-testid="kiosk-liaison-owner">
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-5 h-5 text-orange-600" />
            <p className="text-sm font-bold text-orange-800 uppercase tracking-wider">Liaison obligatoire</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { k: 'projet', l: 'Projet', icon: Briefcase },
              { k: 'semaine', l: 'Semaine', icon: Calendar },
              { k: 'ajustement', l: 'Réajustement', icon: Wrench },
              { k: 'retour_stock', l: 'Entrée stock', icon: Undo2 },
            ].map((t) => {
              const Active = ownerTab === t.k;
              const Icon = t.icon;
              return (
                <button
                  key={t.k}
                  onClick={() => {
                    setOwnerTab(t.k);
                    if (t.k === 'semaine') setLiaison({ type: 'semaine', id: null, label: `Semaine ${currentWeek}`, motif: '' });
                    else if (t.k === 'retour_stock') setLiaison({ type: 'retour_stock', id: null, label: 'Entrée au stock', motif: '' });
                    else if (t.k === 'ajustement') setLiaison({ type: 'ajustement', id: null, label: '', motif: motifInput });
                    else setLiaison(null); // projet : doit choisir
                  }}
                  className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs sm:text-sm font-semibold border-2 transition ${
                    Active ? 'bg-orange-600 text-white border-orange-700 shadow' : 'bg-white text-orange-700 border-orange-200 hover:bg-orange-100'
                  }`}
                  data-testid={`kiosk-liaison-tab-${t.k}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.l}
                </button>
              );
            })}
          </div>

          {ownerTab === 'projet' && renderProjetPicker()}
          {ownerTab === 'semaine' && (
            <div className="bg-white rounded-lg p-3 text-center text-blue-800 font-semibold" data-testid="kiosk-liaison-semaine-info">
              📅 Semaine ISO : <span className="text-xl">{currentWeek}</span>
            </div>
          )}
          {ownerTab === 'ajustement' && (
            <div className="bg-white rounded-lg p-3 space-y-2" data-testid="kiosk-liaison-ajustement">
              <label className="text-xs font-semibold text-slate-700">Motif (obligatoire)</label>
              <Input
                value={motifInput}
                onChange={(e) => {
                  setMotifInput(e.target.value);
                  setLiaison({ type: 'ajustement', id: null, label: '', motif: e.target.value });
                }}
                placeholder="Ex : inventaire annuel, perte/casse, correction"
                className="text-sm"
                data-testid="kiosk-liaison-motif-input"
              />
            </div>
          )}
          {ownerTab === 'retour_stock' && (
            <div className="bg-white rounded-lg p-3 text-center text-emerald-800 font-semibold" data-testid="kiosk-liaison-retour-info">
              ↩️ Le mouvement sera enregistré comme une ENTRÉE (stock incrémenté).
            </div>
          )}
        </div>
      );
    }

    // Peintre / chef : suggestions + recherche
    return (
      <div className="rounded-xl border-2 border-purple-200 bg-purple-50 p-4 space-y-3" data-testid="kiosk-liaison-peintre">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-purple-600" />
          <p className="text-sm font-bold text-purple-800 uppercase tracking-wider">À quel projet liez-vous cette sortie ?</p>
        </div>

        {renderProjetPicker()}
      </div>
    );
  };

  const renderProjetPicker = () => (
    <div className="space-y-3">
      {/* Liaison choisie */}
      {liaison?.type === 'projet' && liaison.id && (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-3 flex items-center justify-between" data-testid="kiosk-liaison-chosen">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <span className="font-bold text-emerald-900">{liaison.label}</span>
          </div>
          <button
            onClick={() => { setLiaison(null); setSearchOpen(false); }}
            className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold underline"
            data-testid="kiosk-liaison-change"
          >
            Changer
          </button>
        </div>
      )}

      {/* Suggestions intelligentes */}
      {!liaison?.id && (
        <>
          {loadingSugs ? (
            <p className="text-sm text-slate-500 italic">Chargement des suggestions…</p>
          ) : suggestions.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="kiosk-liaison-suggestions">
              {suggestions.slice(0, 6).map((s) => {
                // 🔗 [Phase v13.60.11 — 2026-08-15] Card enrichie
                // 🔠 [Phase v13.60.11.1 — 2026-08-15] Nom du BT (description_travaux)
                //   remplace le numéro. Si vide → on affiche juste client + adresse
                //   (pas de placeholder — l'utilisatrice ne veut PAS voir le numéro).
                //   Format :
                //     Ligne 1 : icône 💼 + nom du client (gros)
                //     Ligne 2 : nom du BT (si dispo — sans badge)
                //     Ligne 3 : 📍 adresse + ville (si dispo)
                const adresse = formatAdresseCompacte(s);
                const nomBT = String(s.nom_bt || '').trim();
                return (
                  <button
                    key={s.id}
                    onClick={() => choisirProjet(s)}
                    className="text-left bg-white hover:bg-purple-100 border-2 border-purple-200 hover:border-purple-400 rounded-lg p-2.5 transition"
                    data-testid={`kiosk-liaison-suggestion-${s.id}`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <Briefcase className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
                      <span className="font-bold text-slate-900 text-sm truncate">
                        {s.client_nom || s.nom_dossier || '—'}
                      </span>
                    </div>
                    {nomBT && (
                      <div className="text-xs text-slate-700 pl-5 truncate"
                           data-testid={`kiosk-liaison-suggestion-nom-${s.id}`}>
                        {nomBT}
                      </div>
                    )}
                    {adresse && (
                      <div className="text-[11px] text-slate-600 flex items-start gap-1 mt-0.5 pl-5"
                           data-testid={`kiosk-liaison-suggestion-adresse-${s.id}`}>
                        <span aria-hidden="true">📍</span>
                        <span className="truncate">{adresse}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-500 italic">Aucune suggestion automatique. Utilisez la recherche ↓</p>
          )}

          {/* Recherche libre */}
          {!searchOpen ? (
            <button
              onClick={() => setSearchOpen(true)}
              className="w-full bg-white border-2 border-dashed border-purple-300 hover:border-purple-500 hover:bg-purple-50 rounded-lg py-2.5 text-sm font-semibold text-purple-700 flex items-center justify-center gap-2 transition"
              data-testid="kiosk-liaison-search-open"
            >
              <Search className="w-4 h-4" />
              Chercher un autre projet…
            </button>
          ) : (
            <div className="bg-white rounded-lg border-2 border-purple-300 p-2 space-y-2" data-testid="kiosk-liaison-search">
              <Input
                autoFocus
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Client, adresse ou nom du BT…"
                className="text-sm"
                data-testid="kiosk-liaison-search-input"
              />
              {searching && <p className="text-xs text-slate-500 italic px-2">Recherche…</p>}
              {searchResults.length > 0 && (
                <ul className="max-h-64 overflow-auto space-y-1">
                  {searchResults.map((s) => {
                    // 🔗 [Phase v13.60.11 — 2026-08-15] Item enrichi
                    // 🔠 [Phase v13.60.11.1 — 2026-08-15] Nom du BT remplace le
                    //   badge numéro violet. Si vide → juste client + adresse.
                    const adresse = formatAdresseCompacte(s);
                    const nomBT = String(s.nom_bt || '').trim();
                    return (
                      <li key={s.id}>
                        <button
                          onClick={() => choisirProjet(s)}
                          className="w-full text-left px-2 py-2 rounded hover:bg-purple-100 text-sm flex items-start gap-2 border border-transparent hover:border-purple-200 transition"
                          data-testid={`kiosk-liaison-search-result-${s.id}`}
                        >
                          <Briefcase className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-1" />
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-900 truncate">
                              {s.client_nom || s.nom_dossier || '—'}
                            </div>
                            {nomBT && (
                              <div className="text-xs text-slate-700 truncate mt-0.5"
                                   data-testid={`kiosk-liaison-search-result-nom-${s.id}`}>
                                {nomBT}
                              </div>
                            )}
                            {adresse && (
                              <div className="text-[11px] text-slate-500 flex items-start gap-1 mt-0.5"
                                   data-testid={`kiosk-liaison-search-result-adresse-${s.id}`}>
                                <span aria-hidden="true">📍</span>
                                <span className="truncate">{adresse}</span>
                              </div>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <button
                onClick={() => { setSearchOpen(false); setSearchTerm(''); }}
                className="text-[11px] text-slate-500 hover:text-slate-700 underline px-2"
              >
                Fermer
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  // Texte/couleur du bouton APPROUVER selon état
  const approuverDisabled = validating || !mode || (mode === 'out' && !liaisonComplete());
  const approuverLabel = validating
    ? 'Validation en cours…'
    : (mode === 'out' && !liaisonComplete())
      ? '⛔ Choisir une liaison'
      : `✅ APPROUVER ${totalItems} ${mode === 'out' ? 'SORTIE' : 'ENTRÉE'}${totalItems > 1 ? 'S' : ''}`;

  // 📱 [Phase v13.60.12 — 2026-08-15] Annule le mode actif + vide le panier.
  //   Utilisé par le bouton « × Annuler » du bandeau plein écran.
  const annulerMode = () => {
    if (cart.length > 0 && !window.confirm('Annuler et vider le panier ?')) return;
    setCart([]);
    setLiaison(null);
    setMotifInput('');
    setSearchOpen(false);
    setSearchTerm('');
    setMode(null);
  };

  // ═══════════════════════════════════════════════════════════
  // ÉCRAN INITIAL — Aucun mode actif : card compacte de choix
  // ═══════════════════════════════════════════════════════════
  if (!mode) {
    return (
      <div
        className="rounded-2xl bg-white shadow-md border border-slate-200 p-5"
        data-testid="kiosk-scan-mode"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-orange-500" />
            Mode Scan rapide (panier)
          </h3>
          <button
            onClick={() => setMuted((m) => !m)}
            className="text-slate-400 hover:text-slate-600 transition"
            title={muted ? 'Réactiver le son' : 'Couper le son'}
            data-testid="kiosk-scan-mute"
          >
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>

        {/* Choix du mode */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Button
            onClick={() => setMode('out')}
            variant="outline"
            className="h-24 text-xl font-bold flex flex-col gap-1 border-red-200 text-red-700 hover:bg-red-50 hover:border-red-400"
            data-testid="kiosk-scan-mode-out"
            disabled={validating}
          >
            <ArrowUpFromLine className="w-7 h-7" />
            SORTIE
          </Button>
          <Button
            onClick={() => setMode('return')}
            variant="outline"
            className="h-24 text-xl font-bold flex flex-col gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400"
            data-testid="kiosk-scan-mode-return"
            disabled={validating}
          >
            <ArrowDownToLine className="w-7 h-7" />
            ENTRÉE
          </Button>
        </div>

        <div className="rounded-lg p-3 text-center text-slate-500 bg-slate-50 text-sm">
          Choisissez <strong>SORTIE</strong> ou <strong>ENTRÉE</strong> pour ouvrir
          l&apos;écran de scan plein écran.
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // 📱 [Phase v13.60.12 — 2026-08-15] OVERLAY PLEIN ÉCRAN
  //    Mode actif → écran dédié avec bandeau contextuel très visible.
  //    Inspiration : kiosques industriels (Marché du Store, etc.) où
  //    l'utilisateur voit clairement qu'il est en contexte SCAN.
  // ═══════════════════════════════════════════════════════════
  const isSortie = mode === 'out';
  const overlayBg = isSortie
    ? 'bg-gradient-to-b from-red-950 via-red-900 to-red-950'
    : 'bg-gradient-to-b from-emerald-950 via-emerald-900 to-emerald-950';
  const bandeauBg = isSortie ? 'bg-red-700' : 'bg-emerald-700';
  const modeLabel = isSortie ? 'MODE SORTIE' : 'MODE ENTRÉE';
  const modeDot = isSortie ? '🔴' : '🟢';
  const approuverBg = isSortie
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-emerald-600 hover:bg-emerald-700 text-white';

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col ${overlayBg} text-white overflow-y-auto`}
      data-testid="kiosk-scan-mode-fullscreen"
      role="dialog"
      aria-modal="true"
      aria-label={modeLabel}
    >
      {/* ─── Bandeau haut TRÈS visible ─────────────────────── */}
      <div
        className={`${bandeauBg} shadow-lg border-b-4 border-white/20 sticky top-0 z-10`}
        data-testid="kiosk-scan-banner"
      >
        <div className="flex items-center justify-between px-4 sm:px-8 py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl sm:text-3xl animate-pulse" aria-hidden="true">
              {modeDot}
            </span>
            <div>
              <h1 className="text-xl sm:text-3xl font-black tracking-wide"
                  data-testid="kiosk-scan-banner-title">
                {modeLabel}
              </h1>
              <p className="text-[11px] sm:text-xs text-white/70 uppercase tracking-wider">
                Scannez les codes-barres
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setMuted((m) => !m)}
              className="text-white/60 hover:text-white transition p-2 rounded-lg hover:bg-white/10"
              title={muted ? 'Réactiver le son' : 'Couper le son'}
              data-testid="kiosk-scan-mute"
            >
              {muted ? <VolumeX className="w-5 h-5 sm:w-6 sm:h-6" /> : <Volume2 className="w-5 h-5 sm:w-6 sm:h-6" />}
            </button>
            <button
              onClick={annulerMode}
              className="flex items-center gap-1 px-3 sm:px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-semibold border border-white/20 transition"
              data-testid="kiosk-scan-cancel"
            >
              <span className="text-lg sm:text-xl leading-none">×</span>
              <span className="hidden sm:inline">Annuler</span>
            </button>
          </div>
        </div>
      </div>

      {/* ─── Zone principale ───────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center px-4 sm:px-6 py-6 sm:py-8 w-full max-w-4xl mx-auto">
        {/* Indicateur scan + animation pulse */}
        <div
          className="w-full rounded-2xl bg-white/5 border-2 border-dashed border-white/20 p-6 sm:p-8 text-center backdrop-blur-sm mb-6"
          data-testid="kiosk-scan-status"
        >
          <div className="flex items-center justify-center gap-3 mb-2">
            <ScanLine
              className={`w-10 h-10 sm:w-14 sm:h-14 ${busy ? 'text-amber-300 animate-spin' : 'text-white/80 animate-pulse'}`}
            />
          </div>
          <p className="text-lg sm:text-2xl font-semibold text-white/90">
            {busy ? 'Traitement…' : 'Scanner un code-barres'}
          </p>
          <p className="text-xs sm:text-sm text-white/50 mt-1">
            Le focus reste actif — inutile de recliquer entre les scans
          </p>
        </div>

        {/* Champ caché scan */}
        <input
          ref={inputRef}
          type="text"
          value={buffer}
          onChange={(e) => setBuffer(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          className="sr-only"
          aria-hidden="true"
          data-testid="kiosk-scan-input"
        />

        {/* 🛒 Panier — grand format pour lecture à distance */}
        {cart.length > 0 ? (
          <div className="w-full bg-white/95 text-slate-900 rounded-2xl p-4 sm:p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-base sm:text-lg font-bold text-slate-800">
                🛒 Panier ({totalItems} unité{totalItems > 1 ? 's' : ''} · {cart.length} produit{cart.length > 1 ? 's' : ''})
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={viderPanier}
                className="text-red-600 hover:text-red-700 text-xs"
                data-testid="kiosk-scan-cart-clear"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Vider
              </Button>
            </div>

            <ul className="space-y-2" data-testid="kiosk-scan-cart">
              {cart.map((item) => (
                <li
                  key={item.productId}
                  className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2.5"
                >
                  <span className="flex-1 min-w-0 truncate text-slate-800 font-semibold text-sm sm:text-base">
                    {item.productName}
                  </span>
                  <span className="text-[11px] text-slate-400 hidden sm:inline">stock: {item.stockAvant}</span>
                  <div className="flex items-center gap-1 bg-white rounded-md border border-slate-200">
                    <button
                      onClick={() => ajusterQty(item.productId, -1)}
                      className="px-2 py-1 hover:bg-slate-100 rounded-l-md text-slate-600"
                      data-testid={`kiosk-scan-qty-minus-${item.productId}`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="px-3 font-bold tabular-nums text-lg" data-testid={`kiosk-scan-qty-${item.productId}`}>
                      {item.qty}
                    </span>
                    <button
                      onClick={() => ajusterQty(item.productId, 1)}
                      className="px-2 py-1 hover:bg-slate-100 rounded-r-md text-slate-600"
                      data-testid={`kiosk-scan-qty-plus-${item.productId}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button
                    onClick={() => supprimerLigne(item.productId)}
                    className="text-slate-400 hover:text-red-600 p-1"
                    data-testid={`kiosk-scan-remove-${item.productId}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>

            {/* 🆕 [Phase 18] Étape liaison — visible que si SORTIE */}
            {renderLiaisonStep()}

            {/* Bouton APPROUVER — grand format */}
            <Button
              onClick={approuverPanier}
              disabled={approuverDisabled}
              className={`w-full h-16 sm:h-20 text-lg sm:text-2xl font-black tracking-wide shadow-lg ${
                approuverDisabled
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : approuverBg
              }`}
              data-testid="kiosk-scan-approve"
            >
              <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 mr-2" />
              {approuverLabel}
            </Button>
          </div>
        ) : (
          <div className="w-full text-center text-white/60 text-sm mt-6"
               data-testid="kiosk-scan-empty-cart">
            Aucun article scanné pour le moment.
          </div>
        )}
      </div>
    </div>
  );
}
